/**
 * Bound Record IDs Hook
 * Tracks which records from the repository are currently bound to shapes on the canvas
 */

import { useMemo } from 'react';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { ShapeDataBinding } from '../../data-binding/models/ShapeDataBinding';

/**
 * Binding information for a record
 */
export interface BoundRecordInfo {
  recordId: string;
  shapeIds: string[];
  isNewRecord: boolean;
}

/**
 * Extract data binding from shape (supports top-level/props/attrs storage).
 */
function getShapeDataBinding(shape: any): ShapeDataBinding | null {
  const candidates = [shape?.dataBinding, shape?.props?.dataBinding, shape?.attrs?.dataBinding];
  for (const binding of candidates) {
    if (!binding || typeof binding !== 'object') {
      continue;
    }
    if (!binding.sourceId || !binding.recordId) {
      continue;
    }
    return binding as ShapeDataBinding;
  }
  return null;
}

/**
 * Hook to track which record IDs from a data source are bound to shapes on canvas
 *
 * @param sourceId - Data source ID to filter by
 * @returns Set of record IDs that are bound to shapes
 *
 * @example
 * ```tsx
 * const boundIds = useBoundRecordIds(dataSource?.id);
 *
 * // Check if a record is on canvas
 * const isOnCanvas = boundIds.has(record.id);
 * ```
 */
export function useBoundRecordIds(sourceId: string | undefined): Set<string> {
  const nodes = useDiagramStore((s) => s.nodes);

  return useMemo(() => {
    if (!sourceId) {
      return new Set<string>();
    }

    const boundIds = new Set<string>();

    for (const node of nodes) {
      const binding = getShapeDataBinding(node);
      if (!binding) continue;
      if (binding.sourceId !== sourceId) continue;
      boundIds.add(binding.recordId);
    }

    return boundIds;
  }, [nodes, sourceId]);
}

/**
 * Hook to get detailed binding information for records
 *
 * @param sourceId - Data source ID to filter by
 * @returns Map of record ID to binding info
 */
export function useBoundRecordInfo(
  sourceId: string | undefined
): Map<string, BoundRecordInfo> {
  const nodes = useDiagramStore((s) => s.nodes);

  return useMemo(() => {
    const bindingMap = new Map<string, BoundRecordInfo>();

    if (!sourceId) {
      return bindingMap;
    }

    for (const node of nodes) {
      const binding = getShapeDataBinding(node);
      if (!binding) continue;
      if (binding.sourceId !== sourceId) continue;

      const recordId = binding.recordId;
      const existing = bindingMap.get(recordId);

      if (existing) {
        existing.shapeIds.push(node.id);
      } else {
        bindingMap.set(recordId, {
          recordId,
          shapeIds: [node.id],
          isNewRecord: binding.isNewRecord === true,
        });
      }
    }

    return bindingMap;
  }, [nodes, sourceId]);
}

/**
 * Hook to get the shape ID for a specific record (if bound)
 *
 * @param sourceId - Data source ID
 * @param recordId - Record ID to look up
 * @returns Shape ID if bound, null otherwise
 */
export function useBoundShapeId(
  sourceId: string | undefined,
  recordId: string | undefined
): string | null {
  const nodes = useDiagramStore((s) => s.nodes);

  return useMemo(() => {
    if (!sourceId || !recordId) {
      return null;
    }

    for (const node of nodes) {
      const binding = getShapeDataBinding(node);
      if (!binding) continue;
      if (binding.sourceId === sourceId && binding.recordId === recordId) {
        return node.id;
      }
    }

    return null;
  }, [nodes, sourceId, recordId]);
}
